/**
 * HijackSkillEffect — Lightning tree Pulse path apotheosis active.
 *
 * Target a disrupted enemy and seize their nervous system for 8 seconds.
 * During the hijack the player controls the hijacked enemy exclusively —
 * the player's own body stands still and is vulnerable (no split-input).
 * The hijacked enemy's attacks fire against its former allies (faction
 * inverted). When the hijack expires the target collapses, stunned for
 * 2 seconds, then wakes hostile.
 *
 * Design decision: toggle-style control. The player fully inhabits the
 * target. Player body is immobilised. If the hijacked enemy dies during
 * the hijack, the effect ends early (no stun applied to a dead target).
 *
 * On activation:
 *   1. rollSkillSuccess('hijack') — fizzle at low attunement XP.
 *   2. Break Hide.
 *   3. Query deps.target.findDisruptedEnemy() for nearest disrupted enemy
 *      in range. No valid target → fizzle, no cooldown.
 *   4. Apply 'hijacked' status (8000ms) to the target.
 *   5. Suspend target AI, invert faction flag, hand control to player.
 *   6. Immobilise player body (isActive() → true, integration layer reads).
 *   7. Start cooldown (4000ms, very_long tier).
 *
 * On expiry (or early end from target death):
 *   1. Clear 'hijacked' status.
 *   2. Restore target faction to hostile.
 *   3. Release player body (isActive() → false).
 *   4. If target is still alive: apply stun (2000ms, immunity-gated).
 *
 * Disruption on the target may expire naturally during the hijack — the
 * 'hijacked' status governs the effect, not the disruption status.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy — stun has 300ms immunity window)
 * @see lightning-spec.md § Hijack (`hijack`)
 * @see design-decisions.md § Hijack Control Scheme: Toggle
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Target system types
// ---------------------------------------------------------------------------

/**
 * Result returned by the target acquisition dep. The skill decides status
 * effect application; the dep handles range-finding and disruption checks.
 */
export interface HijackTargetResult {
  /**
   * True if a disrupted enemy was found in range and is available for
   * hijacking. False → fizzle (no cooldown).
   */
  readonly acquired: boolean;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HijackDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  /**
   * Target acquisition: finds the nearest disrupted enemy in range.
   * Returns whether a valid target was acquired.
   */
  readonly target: {
    findDisruptedEnemy(): HijackTargetResult;
  };
  /**
   * Status effects on the hijacked (target) enemy.
   * Used to apply 'hijacked' and post-hijack 'stun'.
   */
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    has(defId: string): boolean;
    isImmune(defId: string): boolean;
    remove(defId: string): void;
  };
  /**
   * Control system for the hijacked enemy. The integration layer wires
   * this to suspend enemy AI, invert faction, and delegate input.
   */
  readonly control: {
    /** Suspend target AI and hand control to player input. */
    beginHijack(): void;
    /** Restore target AI and hostile faction. */
    endHijack(): void;
    /** True if the hijacked target is still alive. */
    isTargetAlive(): boolean;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HijackConfig {
  /** Very long cooldown tier: 4000ms. */
  readonly cooldownMs: number;
  /** Hijack active duration in ms. Spec: 8000ms. */
  readonly baseDurationMs: number;
  /** Post-hijack stun duration in ms. Spec: 2000ms. */
  readonly postHijackStunMs: number;
}

const DEFAULT_CONFIG: HijackConfig = {
  cooldownMs: 4000,
  baseDurationMs: 8000,
  postHijackStunMs: 2000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HijackSkillEffect implements SkillEffect {
  readonly skillId = 'hijack';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: HijackDeps;
  private readonly config: HijackConfig;

  private _cooldownRemaining = 0;

  // Active hijack state — reset on each activation.
  private _isActive = false;
  private _hijackElapsed = 0;

  // Lifetime stats.
  private _activations = 0;
  private _fizzles = 0;
  private _hijacksCompleted = 0;
  private _earlyEnds = 0;
  private _postHijackStuns = 0;

  constructor(deps: HijackDeps, config: Partial<HijackConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for scenario assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  /** True while the player is inhabiting the hijacked enemy. */
  get isHijackActive(): boolean { return this._isActive; }
  get hijackElapsed(): number { return this._hijackElapsed; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get hijacksCompleted(): number { return this._hijacksCompleted; }
  /** Times the hijack ended early (target died). */
  get earlyEnds(): number { return this._earlyEnds; }
  /** Times the post-hijack stun was successfully applied. */
  get postHijackStuns(): number { return this._postHijackStuns; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — low attunement XP.
    if (!this.deps.rollSkillSuccess('hijack')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // Find a disrupted enemy in range.
    const result = this.deps.target.findDisruptedEnemy();
    if (!result.acquired) {
      // No valid target — fizzle without cooldown.
      this._fizzles++;
      return false;
    }

    // Apply 'hijacked' status to the target enemy.
    // STUB: deferred — no enemy AI puppeting system reads this status
    this.deps.statusEffects.apply('hijacked', {
      sourceSkillId: 'hijack',
      durationMs: this.config.baseDurationMs,
    });

    // Suspend enemy AI, invert faction, delegate control to player.
    this.deps.control.beginHijack();

    // Enter active state — player body is immobilised.
    this._isActive = true;
    this._hijackElapsed = 0;

    // Cooldown begins at activation (consistent with other apotheosis skills).
    this._cooldownRemaining = this.config.cooldownMs;

    this._activations++;
    return true;
  }

  /**
   * Hijack cannot be manually cancelled — it runs its full duration or
   * ends early if the target dies. Intentional no-op.
   */
  deactivate(): void {
    // Intentionally empty.
  }

  isActive(): boolean {
    return this._isActive;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick active hijack.
    if (this._isActive) {
      this._hijackElapsed += delta;

      // Check if the hijacked target has died mid-hijack.
      if (!this.deps.control.isTargetAlive()) {
        this._endHijack(false);
        return;
      }

      // Check if the hijack duration has expired.
      if (this._hijackElapsed >= this.config.baseDurationMs) {
        this._endHijack(true);
      }
    }
  }

  /**
   * End the hijack — either from duration expiry or target death.
   * @param targetAlive Whether the target is still alive at end time.
   */
  private _endHijack(targetAlive: boolean): void {
    this._isActive = false;

    // Remove the hijacked status effect.
    this.deps.statusEffects.remove('hijacked');

    // Restore enemy AI, faction, and release player body.
    this.deps.control.endHijack();

    if (targetAlive) {
      // Apply post-hijack stun (immunity-gated — stun has 300ms window).
      if (!this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'hijack',
          durationMs: this.config.postHijackStunMs,
        });
        this._postHijackStuns++;
      }
      this._hijacksCompleted++;
    } else {
      // Target died — no stun (can't stun a corpse).
      this._earlyEnds++;
    }
  }

  destroy(): void {
    if (this._isActive) {
      // Clean end on destroy — release control without stun.
      this.deps.statusEffects.remove('hijacked');
      this.deps.control.endHijack();
      this._isActive = false;
    }
    this._cooldownRemaining = 0;
  }
}
